/**
 * Meus pedidos — o acompanhamento, replicado do sistema antigo por pedido do
 * Lucas ("o fluxo antigo estava ótimo").
 *
 * O que o antigo fazia, e o que fica igual:
 *   - "Em andamento" e "Histórico" separados;
 *   - o status muda para "em análise" quando o CD ABRE o pedido — é como o
 *     solicitante sabe que alguém está olhando;
 *   - separação PARCIAL volta para o solicitante decidir: aceitar como está
 *     (romaneio do que foi separado) ou cancelar;
 *   - recusa aparece com o motivo.
 *
 * A adaptação ao modelo novo: no antigo, cancelar deixava um "rascunho pronto
 * para reenviar". Aqui o rascunho É o carrinho — então o que faltou volta para
 * o carrinho da obra, e reenviar é enviar o carrinho de novo.
 *
 * E a trilha de passos (Enviado → Separação → Retirada → Entrega) mostra EM QUE
 * PASSO O ROMANEIO ESTÁ — que era exatamente o pedido do Lucas.
 */

import { api, ErroApi } from '../../api.js';
import { dataHora, escapar, numero, quando } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';
import { obraAtual, carregarCarrinho } from './estado.js';
import { ativarPush, jaInscrito, suportaPush } from '../../comum/push.js';
import { imprimirRomaneio } from '../../comum/imprimir-romaneio.js';

interface ItemMeu {
  codigo: string;
  nome: string;
  unidade: string | null;
  pedida: number;
  separada: number;
}

interface PedidoMeu {
  numero: string;
  criado_em: string;
  status: string;
  motivo_recusa: string | null;
  obs: string | null;
  obra: string;
  obraNome: string;
  romaneio: string | null;
  romaneioStatus: string | null;
  retirado_em: string | null;
  entregue_em: string | null;
  motorista_nome: string | null;
  recebedor: string | null;
  itens: ItemMeu[];
}

/** O que ainda anda: pedido sem decisão final, ou romaneio ainda na rua. */
function emAndamento(p: PedidoMeu): boolean {
  if (['aguardando_analise', 'em_analise', 'separado_parcial'].includes(p.status)) return true;
  if (['separado_integral', 'aceito'].includes(p.status)) {
    return p.romaneioStatus !== 'entregue' && p.romaneioStatus !== 'cancelado';
  }
  return false;
}

export function montarMeus(raiz: HTMLElement): void {
  let pedidos: PedidoMeu[] = [];
  let aba: 'andamento' | 'historico' = 'andamento';

  raiz.innerHTML = `
    <div id="mp-convite"></div>
    <div class="abas" id="mp-abas">
      <button class="aba" aria-current="true" data-aba="andamento">Em andamento</button>
      <button class="aba" data-aba="historico">Histórico</button>
    </div>
    <div id="mp-lista"><p class="carregando">Carregando seus pedidos…</p></div>`;

  const lista = raiz.querySelector<HTMLElement>('#mp-lista')!;

  // O convite dos AVISOS: com eles ativos, cada mudança de status do pedido
  // sobe uma notificação — no computador e no celular (12/08, Lucas).
  if (suportaPush()) {
    void jaInscrito().then((sim) => {
      if (sim || Notification.permission === 'denied') return;
      const caixa = raiz.querySelector<HTMLElement>('#mp-convite')!;
      caixa.innerHTML = `
        <div class="mot-aviso">🔔 Quer saber na hora quando seu pedido mudar de status
          (em análise, separado, saiu para entrega, entregue)?
          <button class="btn btn-claro" id="mp-ativar-push">Ativar avisos</button>
        </div>`;
      caixa.querySelector('#mp-ativar-push')!.addEventListener('click', () => {
        void ativarPush().then((r) => {
          if (r.estado === 'ativado') {
            caixa.innerHTML = '';
            avisar('Avisos ativados — você recebe cada mudança dos seus pedidos.');
          } else {
            avisarBloqueante(r.detalhe);
          }
        });
      });
    });
  }

  raiz.querySelectorAll<HTMLButtonElement>('#mp-abas .aba').forEach((b) => {
    b.addEventListener('click', () => {
      aba = b.dataset['aba'] as typeof aba;
      raiz
        .querySelectorAll('#mp-abas .aba')
        .forEach((x) => x.setAttribute('aria-current', String(x === b)));
      desenhar();
    });
  });

  async function carregar(): Promise<void> {
    try {
      const r = await api.get<{ pedidos: PedidoMeu[] }>('/api/pedidos/meus');
      pedidos = r.pedidos;
      desenhar();
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar seus pedidos.',
      )}</p>`;
    }
  }

  function desenhar(): void {
    const desta = pedidos.filter((p) => (aba === 'andamento' ? emAndamento(p) : !emAndamento(p)));
    if (desta.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>${
        aba === 'andamento'
          ? 'Nenhum pedido em andamento. O que você enviar aparece aqui.'
          : 'Nada no histórico ainda.'
      }</p></div>`;
      return;
    }
    lista.innerHTML = `<div class="mp-grade">${desta.map(cartao).join('')}</div>`;

    lista.querySelectorAll<HTMLButtonElement>('[data-decidir]').forEach((b) => {
      b.addEventListener('click', () => {
        void decidir(b.dataset['pedido']!, b.dataset['decidir'] as 'aceitar' | 'cancelar');
      });
    });
    lista.querySelectorAll<HTMLButtonElement>('[data-romaneio]').forEach((b) => {
      b.addEventListener('click', () => void verRomaneio(b.dataset['romaneio']!));
    });
  }

  /**
   * O ROMANEIO COMPLETO do pedido (12/08, Lucas): quem pediu vê tudo — itens,
   * fotos de retirada e entrega, quem recebeu, e o MAPA do ponto exato da
   * entrega. O servidor só entrega romaneio de pedido do próprio solicitante.
   */
  async function verRomaneio(numeroRom: string): Promise<void> {
    lista.innerHTML = `<p class="carregando">Abrindo o romaneio ${escapar(numeroRom)}…</p>`;
    interface Rom {
      numero: string;
      status: string;
      criado_em: string;
      retirado_em: string | null;
      entregue_em: string | null;
      motorista_nome: string | null;
      encarregado: string | null;
      recebedor: string | null;
      obs: string | null;
      obraNome: string | null;
      obraEndereco: string | null;
      foto_retirada_url: string | null;
      lat_retirada: number | null;
      lng_retirada: number | null;
      lat_entrega: number | null;
      lng_entrega: number | null;
      itens: { codigo: string; nome: string; nome_no_ato: string | null; unidade: string | null; quantidade: number }[];
      fotosEntrega: string[];
    }
    let r: Rom;
    try {
      r = await api.get<Rom>(`/api/romaneios/${encodeURIComponent(numeroRom)}`);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir o romaneio.',
      )}</p>`;
      return;
    }

    const statusLegivel =
      { aguardando_retirada: 'Aguardando retirada', em_rota: 'Em rota', entregue: 'Entregue ✓', cancelado: 'Cancelado' }[
        r.status
      ] ?? r.status;
    const mapaEntrega =
      r.lat_entrega !== null && r.lng_entrega !== null
        ? { img: api.foto(`/api/fotos/mapa?lat=${r.lat_entrega}&lng=${r.lng_entrega}`), link: `https://www.google.com/maps?q=${r.lat_entrega},${r.lng_entrega}` }
        : null;

    lista.innerHTML = `
      <button class="voltar" id="mp-rom-voltar">← Meus pedidos</button>
      <section class="ped-painel">
        <header class="ficha-topo"><div>
          <span class="rotulo">Romaneio</span>
          <h2>${escapar(r.numero)} — ${escapar(statusLegivel)}</h2>
          <p class="sub">📍 ${escapar(r.obraNome ?? '')}${r.obraEndereco ? ` · ${escapar(r.obraEndereco)}` : ''}</p>
        </div>
        <button class="btn btn-claro" id="mp-rom-imprimir">🖨️ Imprimir</button>
        </header>

        <div class="mp-rom-datas">
          <div><b>Criado</b><span>${dataHora(r.criado_em)}</span></div>
          <div><b>Retirado</b><span>${r.retirado_em ? dataHora(r.retirado_em) : '—'}${r.motorista_nome ? ` · ${escapar(r.motorista_nome)}` : ''}</span></div>
          <div><b>Entregue</b><span>${r.entregue_em ? dataHora(r.entregue_em) : '—'}${r.recebedor ? ` · recebeu: ${escapar(r.recebedor)}` : ''}</span></div>
        </div>

        <h3>Itens <span class="sub-inline">${r.itens.length}</span></h3>
        <div class="rolagem"><table class="tabela"><thead><tr>
          <th>Código</th><th>Item</th><th class="num">Qtde</th>
        </tr></thead><tbody>
          ${r.itens
            .map(
              (i) => `<tr><td class="cod">${escapar(i.codigo)}</td>
                <td>${escapar(i.nome_no_ato ?? i.nome)}</td>
                <td class="num">${numero(i.quantidade)} ${escapar(i.unidade ?? '')}</td></tr>`,
            )
            .join('')}
        </tbody></table></div>

        ${
          r.foto_retirada_url || r.fotosEntrega.length
            ? `<h3>Fotos</h3>
               <div class="mp-rom-fotos">
                 ${r.foto_retirada_url ? `<figure><img src="${escapar(r.foto_retirada_url)}" data-grande="${escapar(r.foto_retirada_url)}" alt=""><figcaption>Retirada</figcaption></figure>` : ''}
                 ${r.fotosEntrega.map((u) => `<figure><img src="${escapar(u)}" data-grande="${escapar(u)}" alt=""><figcaption>Entrega</figcaption></figure>`).join('')}
               </div>`
            : ''
        }

        ${
          mapaEntrega
            ? `<h3>📍 Onde a entrega foi finalizada</h3>
               <a href="${escapar(mapaEntrega.link)}" target="_blank" rel="noopener" class="mp-rom-mapa">
                 <img src="${escapar(mapaEntrega.img ?? '')}" alt="Mapa do ponto da entrega">
               </a>
               <p class="dica">Toque no mapa para abrir no Google Maps.</p>`
            : r.status === 'entregue'
              ? `<p class="dica">A entrega não registrou localização (o motorista não liberou o GPS).</p>`
              : ''
        }
        ${r.obs ? `<p class="dica">Obs.: ${escapar(r.obs)}</p>` : ''}
      </section>`;
    lista.querySelector('#mp-rom-voltar')!.addEventListener('click', () => desenhar());
    lista.querySelector('#mp-rom-imprimir')!.addEventListener('click', () => imprimirRomaneio(r));
  }

  function cartao(p: PedidoMeu): string {
    return `
      <article class="mp-card ${p.status}">
        <header class="mp-topo">
          <b>${escapar(p.numero)}</b>
          <span class="mp-pill ${p.status}">${rotulo(p)}</span>
        </header>
        <div class="mp-meta">📍 ${escapar(p.obraNome)}</div>
        <div class="mp-meta">${quando(p.criado_em)} · ${p.itens.length} item(ns)</div>
        ${trilha(p)}
        ${detalhePorStatus(p)}
      </article>`;
  }

  /**
   * A TRILHA: em que passo o pedido — e depois o romaneio — está.
   * Recusado/cancelado não têm trilha; têm o desfecho.
   */
  function trilha(p: PedidoMeu): string {
    if (p.status === 'recusado' || p.status === 'cancelado') return '';
    const passos: { nome: string; feito: boolean; atual: boolean; nota?: string }[] = [
      { nome: 'Enviado', feito: true, atual: false },
      {
        nome: 'Separação',
        feito: ['separado_integral', 'aceito'].includes(p.status),
        atual: ['aguardando_analise', 'em_analise', 'separado_parcial'].includes(p.status),
        ...(p.status === 'em_analise'
          ? { nota: 'o CD está separando agora' }
          : p.status === 'aguardando_analise'
            ? { nota: 'na fila do CD' }
            : p.status === 'separado_parcial'
              ? { nota: 'parcial — sua decisão' }
              : {}),
      },
      {
        nome: 'Retirada',
        feito: !!p.retirado_em,
        atual: !!p.romaneio && !p.retirado_em && p.romaneioStatus !== 'cancelado',
        ...(p.romaneio && !p.retirado_em && p.romaneioStatus !== 'cancelado'
          ? { nota: p.motorista_nome ? `com ${p.motorista_nome}` : 'aguardando o motorista' }
          : {}),
      },
      {
        nome: 'Entrega',
        feito: !!p.entregue_em,
        atual: p.romaneioStatus === 'em_rota',
        ...(p.romaneioStatus === 'em_rota' ? { nota: 'material na rua' } : {}),
      },
    ];
    return `
      <ol class="mp-trilha">
        ${passos
          .map(
            (s) => `
          <li class="${s.feito ? 'feito' : ''} ${s.atual ? 'atual' : ''}">
            <span class="mp-bola">${s.feito ? '✓' : ''}</span>
            <span class="mp-passo">${s.nome}</span>
            ${s.nota ? `<span class="mp-nota">${escapar(s.nota)}</span>` : ''}
          </li>`,
          )
          .join('')}
      </ol>`;
  }

  function detalhePorStatus(p: PedidoMeu): string {
    if (p.status === 'recusado') {
      return `<div class="mp-motivo"><b>Recusado pelo CD.</b> ${escapar(p.motivo_recusa ?? 'Sem motivo registrado.')}</div>`;
    }
    if (p.status === 'cancelado') {
      return `<div class="mp-motivo">Cancelado por você — o que faltou voltou para o carrinho da obra.</div>`;
    }
    if (p.status === 'separado_parcial') {
      return `
        <div class="mp-parcial">
          ${p.itens
            .map((i) => {
              const falta = i.pedida - i.separada;
              return `<div class="mp-item">${escapar(i.nome)}: pedido ${numero(i.pedida)}, separado ${numero(i.separada)}${
                falta > 0 ? ` <span class="mp-falta">(faltou ${numero(falta)})</span>` : ''
              }</div>`;
            })
            .join('')}
        </div>
        <div class="mp-acoes">
          <button class="btn" data-decidir="aceitar" data-pedido="${escapar(p.numero)}">✓ Aceitar e mandar para entrega</button>
          <button class="btn btn-claro" data-decidir="cancelar" data-pedido="${escapar(p.numero)}">✗ Cancelar — o que faltou volta ao carrinho</button>
        </div>`;
    }
    const linhas: string[] = [];
    if (p.romaneio && p.romaneioStatus !== 'cancelado') {
      linhas.push(`<div class="mp-meta">Romaneio <b>${escapar(p.romaneio)}</b>${
        p.motorista_nome ? ` · motorista: ${escapar(p.motorista_nome)}` : ''
      }</div>
      <div class="mp-acoes">
        <button class="btn btn-claro" data-romaneio="${escapar(p.romaneio)}">📋 Romaneio completo${
          p.entregue_em ? ' (fotos e mapa)' : ''
        }</button>
      </div>`);
    }
    if (p.romaneioStatus === 'cancelado') {
      linhas.push(`<div class="mp-motivo">O romaneio ${escapar(p.romaneio ?? '')} foi cancelado pelo CD — fale com o almoxarifado.</div>`);
    }
    if (p.entregue_em) {
      linhas.push(`<div class="mp-meta">Entregue em ${dataHora(p.entregue_em)}${
        p.recebedor ? ` · recebeu: ${escapar(p.recebedor)}` : ''
      }</div>`);
    }
    return linhas.join('');
  }

  function rotulo(p: PedidoMeu): string {
    if (['separado_integral', 'aceito'].includes(p.status)) {
      if (p.entregue_em) return 'Entregue';
      if (p.romaneioStatus === 'em_rota') return 'Em rota';
      if (p.romaneioStatus === 'cancelado') return 'Romaneio cancelado';
      return 'Aguardando retirada';
    }
    return (
      {
        aguardando_analise: 'Aguardando separação',
        em_analise: 'Em separação',
        separado_parcial: 'Sua decisão',
        recusado: 'Recusado',
        cancelado: 'Cancelado',
      }[p.status] ?? p.status
    );
  }

  async function decidir(numeroPedido: string, decisao: 'aceitar' | 'cancelar'): Promise<void> {
    const pergunta =
      decisao === 'aceitar'
        ? 'Aceitar a separação como está e mandar para entrega?'
        : 'Cancelar este pedido? Os itens que faltaram voltam para o carrinho da obra, prontos para reenviar.';
    if (!confirm(pergunta)) return;
    try {
      const r = await api.post<{ romaneio?: string; devolvidos?: number }>(
        `/api/pedidos/${encodeURIComponent(numeroPedido)}/decisao`,
        { decisao },
      );
      if (decisao === 'aceitar') {
        avisar(`Romaneio ${r.romaneio} criado — acompanhe a entrega aqui.`);
      } else {
        avisar(
          r.devolvidos
            ? `Pedido cancelado. ${r.devolvidos} item(ns) voltaram para o carrinho da obra.`
            : 'Pedido cancelado.',
        );
        // Se a pessoa está NA obra do pedido, o contador do carrinho tem que
        // refletir a volta dos itens agora, não na próxima visita.
        const obra = obraAtual();
        const pedido = pedidos.find((x) => x.numero === numeroPedido);
        if (obra && pedido && obra.codigo === pedido.obra) {
          void carregarCarrinho(obra.codigo).catch(() => {});
        }
      }
      await carregar();
    } catch (erro) {
      avisar(erro instanceof ErroApi ? erro.message : 'Não consegui registrar a decisão.', 'atencao');
    }
  }

  void carregar();
}
